<template>
    <modal
        name="mexport-modal-form"
        id="mexportModalForm"
        class="modal--translation mexport-modal"
        :class="this.sendingExport ? 'loading':''"
        v-cloak height="auto"
        width="680px"
        :scrollable="true"
        :adaptive="true"
        :pivot-y="0.15"
        @before-open="beforeOpen"
        @opened="opened"
        @before-close="beforeClose"
    >
        <div class="modal-header">
            <h5 class="modal-title"><i class="fal fa-fw fa-envelope"></i>{{ trans.export_mrp }}</h5>
            <button @click="close()" type="button" class="close close-modal">
                <i class="fal fa-fw fa-times"></i>
            </button>
        </div>

        <div v-if="isSend == null" class="modal-body">
            <p class="mexport-modal__lead">{{ trans.confirm_label_1 }}</p>

            <dl class="mexport-modal__facts">
                <dt>{{ trans.label_restaurant }}</dt>
                <dd>{{ this.exportSettings.currentRestaurant.title }}</dd>

                <dt>{{ trans.label_period }}</dt>
                <dd>{{ selectedMonthLabel() }}</dd>

                <dt>{{ trans.label_target_email }}</dt>
                <dd>{{ this.exportSettings.targetEmail }}</dd>
            </dl>

            <!-- notes are shown all at once, after data check is finished -->
            <template v-if="!dataLoading">
                <p v-if="this.msgCopyToEmailVisible" class="mexport-modal__note">{{ trans.confirm_label_2 }}: <b>{{ this.exportSettings.copyEmail }}</b></p>
                <p v-if="this.msgBlockingRestaurantsVisible" class="mexport-modal__note">{{ trans.confirm_label_blocking }}: <b>{{ this.msgBlockingRestaurants }}</b></p>
                <p v-if="this.msgNoBlockingRestaurantsVisible && this.currentRestaurantLocked" class="mexport-modal__note">{{ trans.confirm_label_ready_to_send }}</p>
                <p v-if="this.msgNoBlockingRestaurantsVisible && !this.currentRestaurantLocked" class="mexport-modal__note">{{ trans.confirm_label_ready_after_lock }}: <b>{{ this.exportSettings.currentRestaurant.title }}</b></p>
            </template>

            <div v-if="dataLoading" class="mexport-modal__loading">
                <span class="mexport-modal__loading-icon"><i class="fal fa-spin fa-spinner"></i></span>
                <span class="mexport-modal__loading-text">
                    <b>{{ trans.checking_data }}</b>
                    <span>{{ trans.checking_data_note }}</span>
                </span>
                <span class="mexport-modal__loading-bar"><i></i></span>
            </div>

            <export-exclusions-panel
                v-show="!dataLoading"
                v-if="urlPreflight"
                ref="exclusionsPanel"
                :url="urlPreflight"
                :year="selectedYear()"
                :month="selectedMonthNumber()"
                :restaurant-id="exportSettings.currentRestaurant.id"
                @loaded="onExclusionsLoaded"
            ></export-exclusions-panel>
        </div>

        <div v-else class="modal-body">
            <div class="mexport-modal__result" :class="isSend ? 'is-success' : 'is-error'">
                <span class="mexport-modal__result-icon">
                    <i class="fal" :class="isSend ? 'fa-check' : 'fa-times'"></i>
                </span>
                <span class="mexport-modal__result-text">
                    <b>{{ isSend ? trans.success_title : sendResponse.title }}</b>
                    <span>{{ isSend ? (sendResponse.message || trans.export_success) : sendResponse.message }}</span>
                </span>
            </div>
        </div>

        <div v-if="isSend == null" class="modal-footer">
            <span class="mexport-modal__sending" v-if="this.sendingExport">{{ trans.sending }}</span>
            <div class="mexport-modal__actions">
                <button type="button" class="btn btn-link" @click="close()" :disabled="sendingExport">{{ trans.btn_cancel }}</button>
                <button type="button" class="btn btn-primary" @click="submit()" :disabled="submitDisabled" :class="submitDisabled ? 'disabled' : ''">
                    <i class="fal fa-fw mr-1" :class="this.sendingExport ? 'fa-spin fa-spinner' : (this.msgBlockingRestaurantsVisible ? 'fa-inbox' : 'fa-envelope')"></i> {{ btnSaveLabel }}
                </button>
            </div>
        </div>
        <div v-else class="modal-footer">
            <div class="mexport-modal__actions">
                <button type="button" class="btn btn-primary" @click="close()">{{ trans.btn_close }}</button>
            </div>
        </div>

    </modal>
</template>


<script>

    const userLanguage = document.documentElement.lang;

    export default {
        name: 'mexport-modal-form',
        data: function() {
            return {
                sendingExport: false,
                isSend: null,
                msgCopyToEmailVisible: false,
                msgBlockingRestaurantsVisible: false,
                msgBlockingRestaurants: "",
                msgNoBlockingRestaurantsVisible: false,
                currentRestaurantLocked: true,
                btnSaveLabel: "",
                hasExclusions: false,
                preflightLoading: false,
                companyStatusLoading: false,
                sendResponse: {
                    type: 'error',
                    title: '',
                    message: ''
                }
            }
        },

        props: {
            'url': {
                type: String
            },
            'urlCompanySendStatus': {
                type: String
            },
            'urlPreflight': {
                type: String
            },
            'trans': {
                type: Object
            },
            'transMonths': {
                type: Object
            },
            'exportSettings': {
                type: Object,
                default: false
            },
            'selectedMonth': {
                type: String,
                default: false
            }
        },

        created() {
        },

        computed: {
            dataLoading() {
                return this.preflightLoading || this.companyStatusLoading;
            },

            submitBusy() {
                return this.sendingExport || this.dataLoading;
            },

            submitDisabled() {
                return this.submitBusy;
            }
        },

        methods: {

            beforeOpen (event) {
                this.hasExclusions = false;
                this.preflightLoading = !!this.urlPreflight;
                // copy of e-mail is sent to user only when export is not sent at once for whole company
                this.msgCopyToEmailVisible = !this.exportSettings.sendCompanyDataAtOnce;
                this.msgBlockingRestaurantsVisible = false;
                this.msgNoBlockingRestaurantsVisible = false;
                this.updateBtnLabel();

                // blocking restaurants have to be resolved only for export sent at once for whole company
                if (this.exportSettings.sendCompanyDataAtOnce) {
                    this.checkCompanySendStatus();
                }
            },

            opened (event) {
                if (this.urlPreflight && this.$refs.exclusionsPanel) {
                    this.$refs.exclusionsPanel.load();
                } else {
                    this.preflightLoading = false;
                }
            },

            onExclusionsLoaded(result) {
                this.preflightLoading = false;
                this.hasExclusions = !!(result && result.available && result.excluded > 0);
                this.updateBtnLabel();
            },

            updateBtnLabel() {
                if (this.msgBlockingRestaurantsVisible) {
                    this.btnSaveLabel = this.trans.export_prepare_for_submit;
                    return;
                }

                this.btnSaveLabel = this.hasExclusions ? this.trans.btn_save_anyway : this.trans.btn_save;
            },

            selectedYear() {
                return moment(this.selectedMonth, "YYYY-MM").format("YYYY");
            },

            selectedMonthNumber() {
                return moment(this.selectedMonth, "YYYY-MM").format("MM");
            },

            // docs (example): https://github.com/euvl/vue-js-modal/blob/master/demo/src/components/Modal_Error.vue
            beforeClose(event) {
                if(this.sendingExport) {
                    event.stop();
                    return false;
                }
            },

            submit: function () {
                if (this.submitDisabled) {
                    return;
                }

                this.sendingExport = true;
                this.isSend = null;
                this.sendResponse = { type: 'error', title: '', message: '' };

                axios.post(this.url.replace("YEAR", moment(this.selectedMonth,"YYYY-MM").format("YYYY"))
                                          .replace("MONTH", moment(this.selectedMonth,"YYYY-MM").format("MM"))
                                          .replace("RESTAURANT", this.exportSettings.currentRestaurant.id), null)
                    .then((response) => {
                        if(response.data.type == 'error')
                        {
                            this.isSend = false;
                            this.sendResponse = response.data;
                        }
                        else {
                            // message from backend says, whether e-mail was really sent, or export is only prepared (awaiting other restaurants)
                            this.sendResponse = response.data;
                            this.isSend = true;
                        }

                        this.sendingExport = false;
                    }, (error) => {
                        this.sendingExport = false;
                        this.isSend = false;
                    })
            },

            checkCompanySendStatus() {
                this.companyStatusLoading = true;

                axios.post(this.urlCompanySendStatus.replace("YEAR", moment(this.selectedMonth,"YYYY-MM").format("YYYY"))
                                          .replace("MONTH", moment(this.selectedMonth,"YYYY-MM").format("MM"))
                                          .replace("RESTAURANT", this.exportSettings.currentRestaurant.id), null)
                    .then((response) => {
                        this.companyStatusLoading = false;

                        if(response.data.send_company_data_at_once)
                        {
                            this.currentRestaurantLocked = response.data.current_restaurant_locked !== false;
                            if(response.data.can_send_data_now)
                            {
                                this.msgCopyToEmailVisible = false;
                                this.msgBlockingRestaurantsVisible = false;
                                this.msgNoBlockingRestaurantsVisible = true;
                            }
                            else {
                                this.msgCopyToEmailVisible = false;
                                this.msgBlockingRestaurantsVisible = true;
                                this.msgNoBlockingRestaurantsVisible = false;
                                // current restaurant is listed too, when its attendance is not locked yet
                                this.msgBlockingRestaurants = (this.currentRestaurantLocked ? [] : [this.exportSettings.currentRestaurant.title])
                                    .concat(response.data.blocking_restaurants)
                                    .sort((a, b) => a.localeCompare(b, userLanguage))
                                    .join(", ");
                            }
                        }
                        else {
                            this.msgCopyToEmailVisible = true;
                            this.msgBlockingRestaurantsVisible = false;
                            this.msgNoBlockingRestaurantsVisible = false;
                        }
                        this.updateBtnLabel();
                    }, (error) => {
                        this.companyStatusLoading = false;
                        this.isSend = false;
                        this.$notify({type: 'error', title: 'Chyba', text: "Error during resolution of export send status ability." });
                    })
            },

            onSuccess(response) {
                this.sendingExport = false;
                this.isSend = true;

                this.$notify({ type: 'success', title: 'Hotovo', text: (this.msgBlockingRestaurantsVisible ? this.trans.export_preparation_success : this.trans.export_success) });
            },

            onFail(error) {
                this.sendingExport = false;
                this.isSend = false;

                let errorMsg = error.response.data.message ? error.response.data.message : 'An error has occured.';

                if(error.response.data.errors)  {
                    errorMsg += Object.keys(error.response.data.errors).map(function (key) {
                        return '<br />'+error.response.data.errors[key][0];
                    });
                }

                this.$notify({ type: 'error', title: 'Chyba', text: errorMsg });
            },

            close() {
                this.$modal.hide('mexport-modal-form');
                this.isSend = null;
            },

            selectedMonthLabel() {
                let m = parseInt(moment(this.selectedMonth, "YYYY-MM").format("M"));
                let y = parseInt(moment(this.selectedMonth, "YYYY-MM").format("YYYY"));

                return this.transMonths[m]+" "+y;
            }
        },
    };
</script>



// WEBPACK FOOTER //
// resources/assets/admin/js/attendance/modals/MexportModalForm.vue